import React from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  saveDiscovery,
  searchDiscovery,
  DiscoveryGrant,
  DiscoverySearchResponse,
  getFundingAmountsMeta,
  getDeadlinesMeta,
  getLocationsMeta,
  getFunderTypesMeta,
  getSectorsMeta,
  MetaOption,
  FundingAmountOption,
  deleteSavedGrant,
  getOrderingOptionsMeta,
  OrderingOption,
  getNormalizedTownsMeta,
  getNormalizedCitiesMeta,
  getNormalizedCountriesMeta,
  aiSearchDiscoverList,
} from "../../api/discovery";
import { getGrantFullDetails } from "../../api/grantWriting";
import toast from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import PrivateFundingWorkspace from "../../features/privateFunding/PrivateFundingWorkspace";

// ─────────────────────────────────────────────────────────────────────────────
// SESSION STORAGE STRATEGY
//
// We distinguish two cases:
//   A) True browser reload  → filters should RESET (fresh start)
//   B) SPA back-navigation  → filters should RESTORE (including filtered count)
//
// How we detect them:
//   - Before navigating away (onStart / any link), we call `markNavAway()`.
//     This sets NAV_FLAG in sessionStorage.
//   - On mount, if NAV_FLAG is present → we came back via SPA nav → RESTORE.
//   - If NAV_FLAG is absent → true reload (or first visit) → RESET.
//   - We immediately clear NAV_FLAG after reading it so the next mount is fresh.
// ─────────────────────────────────────────────────────────────────────────────

const STATE_KEY = "discover_state_v4";
const SCROLL_KEY = "discover_scroll_v4";
const NAV_FLAG = "discover_nav_flag_v4";

interface PersistedState {
  q: string;
  funding: string;
  deadline: string;
  locations: string[];
  funderTypes: string[];
  sectors: string[];
  towns: string[];
  cities: string[];
  countries: string[];
  ordering: string;
  page: number;
  count: number;
  aiResults: DiscoveryGrant[] | null;
}

const DEFAULT: PersistedState = {
  q: "", funding: "Any", deadline: "Any",
  locations: [], funderTypes: [], sectors: [], towns: [], cities: [], countries: [],
  ordering: "", page: 1, count: 0, aiResults: null,
};

function savePersistedState(s: PersistedState) {
  try { sessionStorage.setItem(STATE_KEY, JSON.stringify(s)); } catch {}
}

function readPersistedState(): PersistedState | null {
  try {
    const raw = sessionStorage.getItem(STATE_KEY);
    return raw ? (JSON.parse(raw) as PersistedState) : null;
  } catch { return null; }
}

/** Call this BEFORE navigating away so back-nav can restore state */
function markNavAway() {
  sessionStorage.setItem(NAV_FLAG, "1");
}

// ─────────────────────────────────────────────────────────────────────────────
// SKELETONS
// ─────────────────────────────────────────────────────────────────────────────

const SkeletonGrantCard = () => (
  <div className="rounded-[10px] border border-[#E2E5EA] bg-white shadow-sm p-4 animate-pulse">
    <div className="h-6 bg-gray-200 rounded w-3/4 mb-3" />
    <div className="flex flex-wrap gap-2 mb-3">
      <div className="h-7 w-20 bg-gray-200 rounded-full" />
      <div className="h-7 w-24 bg-gray-200 rounded-full" />
      <div className="h-7 w-16 bg-gray-200 rounded-full" />
    </div>
    <div className="space-y-2 mb-3">
      <div className="h-4 bg-gray-200 rounded w-full" />
      <div className="h-4 bg-gray-200 rounded w-5/6" />
      <div className="h-4 bg-gray-200 rounded w-4/6" />
    </div>
    <div className="flex flex-wrap items-center justify-between gap-6">
      <div className="h-4 w-32 bg-gray-200 rounded" />
      <div className="h-4 w-20 bg-gray-200 rounded" />
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 bg-gray-200 rounded-lg" />
        <div className="h-9 w-32 bg-gray-200 rounded-lg" />
      </div>
    </div>
  </div>
);

const SkeletonFilters = () => (
  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 animate-pulse">
    {[...Array(8)].map((_, i) => (
      <div key={i}>
        <div className="h-4 w-24 bg-gray-200 rounded mb-1" />
        <div className="h-10 bg-gray-200 rounded-lg" />
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// UI PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────

const Tag = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center gap-1 rounded-[999px] bg-black/[0.06] font-medium px-2.5 py-1 text-sm">
    <span className="h-[6px] w-[6px] rounded-full bg-[#6B7280]" />
    {children}
  </span>
);

// ─────────────────────────────────────────────────────────────────────────────
// HOVER CARD
// ─────────────────────────────────────────────────────────────────────────────

function HoverCard({ grant, anchorEl }: { grant: DiscoveryGrant; anchorEl: HTMLElement | null }) {
  const [pos, setPos] = React.useState<{ top: number; left: number; width: number } | null>(null);

  React.useEffect(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const vw = window.innerWidth;
    const w = Math.min(360, vw - 32);
    let left = r.right + 12;
    if (left + w > vw - 8) left = r.left - w - 12;
    if (left < 8) left = 8;
    setPos({ top: r.top, left, width: w });
  }, [anchorEl]);

  if (!pos) return null;

  const tags = [grant.funder, grant.applicant_type, grant.location, (grant as any).city, (grant as any).country].filter(Boolean) as string[];
  const amount = grant.total_fund_amount != null ? String(grant.total_fund_amount)
    : grant.min_award != null || grant.max_award != null ? `${grant.min_award ?? 0}${grant.max_award ? " – " + grant.max_award : ""}` : "—";
  const deadline = grant.close_date ? new Date(grant.close_date).toLocaleDateString() : "—";
  const allWords = (grant.description ?? "").trim().split(/\s+/);
  const desc = allWords.slice(0, 60).join(" ") + (allWords.length > 60 ? "…" : "");

  return createPortal(
    <div style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width, zIndex: 10000 }}
      className="rounded-xl border border-[#E2E5EA] bg-white shadow-2xl p-4 pointer-events-none">
      <div className="text-[14px] font-semibold text-[#0B1F17] leading-snug mb-2">{grant.title}</div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {tags.map((t) => <span key={t} className="text-[11px] bg-black/[0.05] rounded-full px-2 py-0.5 text-[#374151]">{t}</span>)}
      </div>
      {desc && <p className="text-[12px] text-[#6B7280] leading-snug mb-3">{desc}</p>}
      <div className="flex items-center justify-between text-[12px] text-[#374151]">
        <span className="flex items-center gap-1"><span className="material-icons text-[14px]">schedule</span>{deadline}</span>
        <span className="flex items-center gap-1 font-semibold"><span className="material-icons text-[14px]">currency_pound</span>{amount}</span>
      </div>
    </div>,
    document.body
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

type DeadlineOption = { label: string; value: string; start: string | null; end: string | null };

function parseDeadline(value: string, options: DeadlineOption[]) {
  if (!value || value === "Any") return {};
  const sel = options.find((o) => o.value === value || o.label === value);
  if (!sel) return {};
  const p: Record<string, any> = {};
  if (sel.start !== null) p.close_date__gte = sel.start;
  if (sel.end !== null) p.close_date__lte = sel.end;
  return p;
}

function SelectBlock({ label, options, value, onChange }: {
  label: string;
  options: (MetaOption | FundingAmountOption | DeadlineOption)[];
  value: string;
  onChange: (v: string) => void;
  // ── onSearch removed — search is now triggered by useEffect ──
}) {
  const isAny = !value || value === "Any";
  return (
    <label className="block">
      <div className="text-md text-[#777B7F] mb-1">{label}</div>
      <div className="relative">
        <select value={value}
          onChange={(e) => { onChange(e.target.value); }}
          className={`h-10 w-full cursor-pointer appearance-none rounded-lg border border-[#D6DADF] bg-white pr-9 pl-3 text-sm outline-none focus:border-[#C7CBCF] ${isAny ? "text-[#9AA0A6]" : "text-[#111]"}`}>
          <option value="" disabled>{`Select ${label.toLowerCase()}`}</option>
          {options.map((opt, idx) => {
            const lbl = typeof opt === "string" ? opt : (opt as any).label ?? (opt as any).value ?? "";
            const val = typeof opt === "string" ? opt : (opt as any).value ?? (opt as any).label ?? "";
            return <option key={val || idx} value={val} style={{ color: val === "Any" ? "#9AA0A6" : "#111" }}>{lbl}</option>;
          })}
        </select>
        <span className="material-icons pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[18px] text-[#6B7280]">expand_more</span>
      </div>
    </label>
  );
}

function useOnClickOutside(refs: Array<React.RefObject<HTMLElement | null>>, cb: () => void) {
  React.useEffect(() => {
    const h = (e: MouseEvent | TouchEvent) => {
      if (refs.some((r) => r.current?.contains(e.target as Node))) return;
      cb();
    };
    document.addEventListener("mousedown", h);
    document.addEventListener("touchstart", h);
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("touchstart", h); };
  }, [refs, cb]);
}

function CheckboxMultiSelect({ label, options, values, onChange, placeholder = "Any", onDone }: {
  label: string; options: string[]; values: string[]; onChange: (v: string[]) => void; placeholder?: string;
  // ── onDone is called when the user clicks "Done" ──
  onDone?: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const rootRef = React.useRef<HTMLDivElement>(null);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState({ top: 0, left: 0, width: 320, maxHeight: 320 });
  const [positioned, setPositioned] = React.useState(false);

  const place = React.useCallback(() => {
    const btn = btnRef.current; if (!btn) return;
    const r = btn.getBoundingClientRect(), vw = window.innerWidth, vh = window.innerHeight, G = 8;
    const w = Math.min(Math.max(300, r.width), vw - 16);
    let left = r.left; if (left + w + G > vw) left = vw - w - G; if (left < G) left = G;
    let top = r.bottom + G, maxH = Math.min(360, vh - r.bottom - G * 2);
    if (maxH < 220) { maxH = Math.min(360, r.top - G * 2); top = Math.max(G, r.top - maxH - G); }
    setPos({ top, left, width: w, maxHeight: maxH }); setPositioned(true);
  }, []);

  React.useEffect(() => {
    if (!open) { setPositioned(false); return; }
    place();
    window.addEventListener("resize", place); window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open, place]);

  useOnClickOutside([rootRef, menuRef], () => setOpen(false));

  const actual = React.useMemo(() => options.filter((o) => o !== "Select All"), [options]);
  const filtered = React.useMemo(() => !q.trim() ? actual : actual.filter((o) => o.toLowerCase().includes(q.toLowerCase())), [actual, q]);
  const allSelected = values.length === actual.length && actual.length > 0;
  const toggle = (opt: string) => { const s = new Set(values); s.has(opt) ? s.delete(opt) : s.add(opt); onChange(Array.from(s)); };
  const summary = values.length === 0 ? placeholder : values.length <= 2 ? values.join(", ") : `${values.slice(0, 2).join(", ")} +${values.length - 2}`;

  return (
    <div className="relative" ref={rootRef}>
      <div className="text-md text-[#777B7F] mb-1">{label}</div>
      <button ref={btnRef} type="button" onClick={() => setOpen((s) => !s)}
        className="h-10 w-full rounded-lg border border-[#D6DADF] bg-white px-3 text-left text-sm flex items-center justify-between">
        <span className={`truncate ${values.length ? "text-[#111]" : "text-[#9AA0A6]"}`}>{summary}</span>
        <span className="material-icons text-[18px] text-[#6B7280]">expand_more</span>
      </button>
      {open && createPortal(
        <div ref={menuRef} onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}
          style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width, maxWidth: "calc(100vw - 16px)", zIndex: 9999, opacity: positioned ? 1 : 0, visibility: positioned ? "visible" : "hidden" }}
          className="rounded-lg border border-[#E2E5EA] bg-white shadow-lg overflow-hidden transition-opacity duration-75">
          <div className="p-2 border-b border-[#EEF1F4]">
            <div className="flex items-center h-9 rounded-md border border-[#D6DADF] px-2 bg-white">
              <span className="material-icons text-[#9AA0A6] text-[18px] mr-1">search</span>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`} className="w-full outline-none text-sm" />
            </div>
          </div>
          <div className="py-1 overflow-auto overflow-x-hidden" style={{ maxHeight: pos.maxHeight }}>
            <label className="flex items-center gap-2 px-3 py-2 text-sm font-semibold hover:bg-black/[0.03] cursor-pointer border-b border-[#EEF1F4]">
              <input type="checkbox" checked={allSelected} onChange={() => allSelected ? onChange([]) : onChange([...actual])} className="h-4 w-4 accent-[#0077B6] shrink-0" />
              <span>Select All</span>
            </label>
            {filtered.length === 0 && <div className="px-3 py-2 text-sm text-[#6B7280]">No options</div>}
            {filtered.map((opt) => (
              <label key={opt} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-black/[0.03] cursor-pointer" title={opt}>
                <input type="checkbox" checked={values.includes(opt)} onChange={() => toggle(opt)} className="h-4 w-4 accent-[#0077B6] shrink-0" />
                <span className="truncate flex-1 min-w-0">{opt}</span>
              </label>
            ))}
          </div>
          <div className="flex items-center justify-between px-3 py-2 border-t border-[#EEF1F4]">
            <div className="text-xs text-[#6B7280]">{values.length ? `${values.length} selected` : placeholder}</div>
            <div className="flex gap-2">
              <button type="button" onClick={() => onChange([])} className="text-sm underline underline-offset-2">Clear</button>
              <button type="button" onClick={() => { setOpen(false); if (onDone) onDone(); }} className="h-8 px-3 rounded-md bg-primary text-sm font-medium">Done</button>
            </div>
          </div>
        </div>, document.body
      )}
    </div>
  );
}

function Pagination({ page, totalPages, onPrev, onNext, onJump }: {
  page: number; totalPages: number; onPrev: () => void; onNext: () => void; onJump: (p: number) => void;
}) {
  if (totalPages <= 1) return null;
  const pages: number[] = [];
  const start = Math.max(1, page - 2), end = Math.min(totalPages, page + 2);
  for (let p = start; p <= end; p++) pages.push(p);
  const btn = (p: number, label?: React.ReactNode) => (
    <button key={String(p)} onClick={() => onJump(p)}
      className={`px-3 py-1 rounded border ${p === page ? "bg-primary text-white border-primary" : "border-[#D6DADF] bg-white"}`}>
      {label ?? p}
    </button>
  );
  return (
    <div className="mt-6 flex flex-wrap items-center justify-center gap-2 text-sm">
      <button onClick={onPrev} disabled={page <= 1} className="px-3 py-1 rounded border border-[#D6DADF] bg-white disabled:opacity-50">Prev</button>
      {start > 1 && <>{btn(1)}{start > 2 && <span className="px-1 text-[#777B7F]">…</span>}</>}
      {pages.map((p) => btn(p))}
      {end < totalPages && <>{end < totalPages - 1 && <span className="px-1 text-[#777B7F]">…</span>}{btn(totalPages)}</>}
      <button onClick={onNext} disabled={page >= totalPages} className="px-3 py-1 rounded border border-[#D6DADF] bg-white disabled:opacity-50">Next</button>
    </div>
  );
}

// misc
const uniq = (arr: string[]) => Array.from(new Set(arr.filter(Boolean)));
const toStringList = (arr: (MetaOption | string)[]) => uniq(arr.map((x) => (typeof x === "string" ? x : (x as any).label ?? (x as any).value ?? "")));
const arraysEqual = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);
const pruneSelected = (sel: string[], opts: string[]) => { const s = new Set(opts.filter((o) => o && o !== "Select All" && o !== "Any")); return sel.filter((v) => s.has(v)); };
const stableKey = (pfx: string, p: Record<string, any>) => `${pfx}?${Object.keys(p).sort().map((k) => `${k}=${p[k]}`).join("&")}`;

function deriveFundingMinMax(val: string, opts: FundingAmountOption[]) {
  if (!val || val === "Any") return {};
  const r = opts.find((o) => o.value === val || o.label === val);
  if (!r) return {};
  const out: { min?: number; max?: number } = {};
  if (typeof r.min === "number") out.min = r.min;
  if (typeof r.max === "number") out.max = r.max;
  return out;
}

function normalizeScore(v: any): number | null {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Number(Math.max(0, Math.min(100, n <= 1 ? n * 100 : n)).toFixed(2));
}

function aiReasonChips(g: any): string[] {
  const raw = g?.ai_reasons ?? g?.reasons ?? g?.reason ?? g?.matched_fields ?? g?.keywords ?? g?.topics ?? g?.tags ?? [];
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean).slice(0, 5);
  if (typeof raw === "string") return raw.split(/[,|]/).map((s) => s.trim()).filter(Boolean).slice(0, 5);
  return [];
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function Discover() {
  const { user } = useAuth();
  const nav = useNavigate();
  const userKey = user?.uid || user?.email || "anonymous";

  // ── Decide whether to restore or reset on mount ──────────────────────────
  const initState = React.useMemo<PersistedState>(() => {
    const isBackNav = sessionStorage.getItem(NAV_FLAG) === "1";
    sessionStorage.removeItem(NAV_FLAG);

    if (isBackNav) {
      return readPersistedState() ?? DEFAULT;
    }
    sessionStorage.removeItem(STATE_KEY);
    return DEFAULT;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Filter option lists ───────────────────────────────────────────────────
  const [fundingOpts, setFundingOpts] = React.useState<FundingAmountOption[]>([{ label: "Any", value: "Any", min: null, max: null }]);
  const [deadlineOpts, setDeadlineOpts] = React.useState<DeadlineOption[]>([{ label: "Any", value: "Any", start: null, end: null }]);
  const [locationOpts, setLocationOpts] = React.useState<string[]>([]);
  const [funderTypeOpts, setFunderTypeOpts] = React.useState<string[]>([]);
  const [sectorOpts, setSectorOpts] = React.useState<string[]>([]);
  const [townOpts, setTownOpts] = React.useState<string[]>([]);
  const [cityOpts, setCityOpts] = React.useState<string[]>([]);
  const [countryOpts, setCountryOpts] = React.useState<string[]>([]);
  const [orderingOpts, setOrderingOpts] = React.useState<OrderingOption[]>([{ label: "Relevance", value: "" }]);

  // ── Selected filter values (seeded from initState) ────────────────────────
  const [q, setQ] = React.useState(initState.q);
  const [funding, setFunding] = React.useState(initState.funding);
  const [deadline, setDeadline] = React.useState(initState.deadline);
  const [locations, setLocations] = React.useState<string[]>(initState.locations);
  const [funderTypes, setFunderTypes] = React.useState<string[]>(initState.funderTypes);
  const [sectors, setSectors] = React.useState<string[]>(initState.sectors);
  const [towns, setTowns] = React.useState<string[]>(initState.towns);
  const [cities, setCities] = React.useState<string[]>(initState.cities);
  const [countries, setCountries] = React.useState<string[]>(initState.countries);
  const [ordering, setOrdering] = React.useState(initState.ordering);
  const [page, setPage] = React.useState(initState.page);

  // ── Results ───────────────────────────────────────────────────────────────
  const [loading, setLoading] = React.useState(false);
  const [filtersLoaded, setFiltersLoaded] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [results, setResults] = React.useState<DiscoveryGrant[]>([]);
  const [count, setCount] = React.useState(initState.count);
  const [pageSize] = React.useState(10);

  const [aiLoading, setAiLoading] = React.useState(false);
  const [aiResults, setAiResults] = React.useState<DiscoveryGrant[] | null>(initState.aiResults);
  const [resetCounter, setResetCounter] = React.useState(0);

  const [savedIds, setSavedIds] = React.useState<Set<string | number>>(new Set());
  const [savedMap, setSavedMap] = React.useState<Map<string | number, number | string>>(new Map());
  const [savingId, setSavingId] = React.useState<string | number | null>(null);
  const [startingId, setStartingId] = React.useState<string | number | null>(null);
  const [expandedDesc, setExpandedDesc] = React.useState<Record<string | number, boolean>>({});

  // ── Hover card ────────────────────────────────────────────────────────────
  const [hoveredGrant, setHoveredGrant] = React.useState<DiscoveryGrant | null>(null);
  const [hoverAnchorEl, setHoverAnchorEl] = React.useState<HTMLElement | null>(null);
  const hoverTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const metaReqRef = React.useRef(0);
  const metaCacheRef = React.useRef<Map<string, any>>(new Map());

  // ── Track whether mount search has already run ────────────────────────────
  const mountedRef = React.useRef(false);

  // ── Persist state on every meaningful change ──────────────────────────────
  React.useEffect(() => {
    savePersistedState({ q, funding, deadline, locations, funderTypes, sectors, towns, cities, countries, ordering, page, count, aiResults });
  }, [q, funding, deadline, locations, funderTypes, sectors, towns, cities, countries, ordering, page, count, aiResults]);

  // ── Build search params ───────────────────────────────────────────────────
  const buildParams = React.useCallback((pg: number, ordOverride?: string) => {
    const p: Record<string, any> = { page: pg, page_size: pageSize };
    const join = (v: string[]) => v.map((s) => s.trim()).filter(Boolean).join(";");
    if (q.trim()) p.search = q.trim();
    if (locations.length) p.location__in = join(locations);
    if (funderTypes.length) p.funder__in = join(funderTypes);
    if (sectors.length) p.applicant_type__in = join(sectors);
    if (towns.length) p.town__in = join(towns);
    if (cities.length) p.city__in = join(cities);
    if (countries.length) p.country__in = join(countries);
    const { min, max } = deriveFundingMinMax(funding, fundingOpts);
    if (typeof min === "number") p.total_funding_min = min;
    if (typeof max === "number") p.total_funding_max = max;
    Object.assign(p, parseDeadline(deadline, deadlineOpts));
    const ord = ordOverride ?? ordering;
    if (ord) p.ordering = ord;
    return p;
  }, [q, locations, funderTypes, sectors, towns, cities, countries, funding, fundingOpts, deadline, deadlineOpts, ordering, pageSize]);

  const buildMetaParams = React.useCallback((exclude: string[] = []) => {
    const p: Record<string, any> = {};
    const join = (v: string[]) => v.map((s) => s.trim()).filter(Boolean).join(";");
    if (q.trim() && !exclude.includes("search")) p.search = q.trim();
    if (locations.length && !exclude.includes("location__in")) p.location__in = join(locations);
    if (funderTypes.length && !exclude.includes("funder__in")) p.funder__in = join(funderTypes);
    if (sectors.length && !exclude.includes("applicant_type__in")) p.applicant_type__in = join(sectors);
    if (towns.length && !exclude.includes("town__in")) p.town__in = join(towns);
    if (cities.length && !exclude.includes("city__in")) p.city__in = join(cities);
    if (countries.length && !exclude.includes("country__in")) p.country__in = join(countries);
    const { min, max } = deriveFundingMinMax(funding, fundingOpts);
    if (typeof min === "number" && !exclude.includes("total_funding_min")) p.total_funding_min = min;
    if (typeof max === "number" && !exclude.includes("total_funding_max")) p.total_funding_max = max;
    const dl = parseDeadline(deadline, deadlineOpts);
    Object.keys(dl).forEach((k) => { if (!exclude.includes(k)) p[k] = (dl as any)[k]; });
    return p;
  }, [q, locations, funderTypes, sectors, towns, cities, countries, funding, fundingOpts, deadline, deadlineOpts]);

  // ── doSearch ──────────────────────────────────────────────────────────────
  const doSearch = React.useCallback(async (pg = 1, ordOverride?: string) => {
    try {
      setLoading(true);
      setError(null);
      setAiResults(null);
      const data: DiscoverySearchResponse = await searchDiscovery(buildParams(pg, ordOverride));
      setResults(data.results);
      setCount(data.count);
      setPage(pg);
      setSavedIds(new Set(data.results.filter((r: any) => r?.saved === true).map((r) => r.id)));
      setSavedMap((prev) => {
        const m = new Map(prev);
        data.results.forEach((r: any) => {
          const sgid = r?.saved_grant_id ?? r?.saved_grant?.id ?? r?.saved_id ?? null;
          if (r?.saved === true && sgid != null) m.set(r.id, sgid);
        });
        return m;
      });
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.response?.data?.message ||  "Failed to load grants");
      setResults([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildParams]);

  // ── On mount ──────────────────────────────────────────────────────────────
  React.useEffect(() => {
    const savedScroll = sessionStorage.getItem(SCROLL_KEY);
    if (savedScroll) {
      const pos = parseInt(savedScroll, 10);
      setTimeout(() => { window.scrollTo(0, pos); sessionStorage.removeItem(SCROLL_KEY); }, 200);
    }

    if (initState.aiResults?.length) return;

    mountedRef.current = true;
    doSearch(initState.page, initState.ordering || undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Ordering options ──────────────────────────────────────────────────────
  React.useEffect(() => {
    let alive = true;
    getOrderingOptionsMeta().then((d) => { if (alive && d?.length) setOrderingOpts(d); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // ── Meta filters (debounced, cached) ─────────────────────────────────────
  React.useEffect(() => {
    const t = window.setTimeout(async () => {
      const reqId = ++metaReqRef.current;
      const cached = async <T,>(key: string, fn: () => Promise<T>): Promise<T> => {
        if (metaCacheRef.current.has(key)) return metaCacheRef.current.get(key);
        const v = await fn(); metaCacheRef.current.set(key, v); return v;
      };
      const [locR, funderR, sectorR, townR, cityR, countryR, fundingR, deadlineR] = await Promise.allSettled([
        cached(stableKey("loc", buildMetaParams(["location__in"])), () => getLocationsMeta(buildMetaParams(["location__in"]))),
        cached(stableKey("funder", buildMetaParams(["funder__in"])), () => getFunderTypesMeta(buildMetaParams(["funder__in"]))),
        cached(stableKey("sector", buildMetaParams(["applicant_type__in"])), () => getSectorsMeta(buildMetaParams(["applicant_type__in"]))),
        cached(stableKey("town", buildMetaParams(["town__in"])), () => getNormalizedTownsMeta(buildMetaParams(["town__in"]))),
        cached(stableKey("city", buildMetaParams(["city__in"])), () => getNormalizedCitiesMeta(buildMetaParams(["city__in"]))),
        cached(stableKey("country", buildMetaParams(["country__in"])), () => getNormalizedCountriesMeta(buildMetaParams(["country__in"]))),
        cached(stableKey("funding", buildMetaParams(["total_funding_min", "total_funding_max"])), () => getFundingAmountsMeta(buildMetaParams(["total_funding_min", "total_funding_max"]))),
        cached(stableKey("deadline", buildMetaParams(["close_date__gte", "close_date__lte"])), () => getDeadlinesMeta(buildMetaParams(["close_date__gte", "close_date__lte"]))),
      ]);
      if (reqId !== metaReqRef.current) return;

      const nLoc = locR.status === "fulfilled" ? uniq(locR.value as string[]) : locationOpts;
      const nFunder = funderR.status === "fulfilled" ? uniq(funderR.value as string[]) : funderTypeOpts;
      const nSector = sectorR.status === "fulfilled" ? uniq(toStringList(sectorR.value as any)) : sectorOpts;
      const nTown = townR.status === "fulfilled" ? uniq(townR.value as string[]) : townOpts;
      const nCity = cityR.status === "fulfilled" ? uniq(cityR.value as string[]) : cityOpts;
      const nCountry = countryR.status === "fulfilled" ? uniq(countryR.value as string[]) : countryOpts;

      setLocationOpts(nLoc); setFunderTypeOpts(nFunder); setSectorOpts(nSector);
      setTownOpts(nTown); setCityOpts(nCity); setCountryOpts(nCountry);
      setFiltersLoaded(true);

      if (fundingR.status === "fulfilled") {
        const o = fundingR.value as FundingAmountOption[];
        setFundingOpts(o);
        setFunding((p) => (p === "Any" || o.some((x) => x.value === p || x.label === p)) ? p : "Any");
      }
      if (deadlineR.status === "fulfilled") {
        const o = deadlineR.value as DeadlineOption[];
        setDeadlineOpts(o);
        setDeadline((p) => (p === "Any" || o.some((x) => x.value === p || x.label === p)) ? p : "Any");
      }
      setLocations((p) => { const n = pruneSelected(p, nLoc); return arraysEqual(p, n) ? p : n; });
      setFunderTypes((p) => { const n = pruneSelected(p, nFunder); return arraysEqual(p, n) ? p : n; });
      setSectors((p) => { const n = pruneSelected(p, nSector); return arraysEqual(p, n) ? p : n; });
      setTowns((p) => { const n = pruneSelected(p, nTown); return arraysEqual(p, n) ? p : n; });
      setCities((p) => { const n = pruneSelected(p, nCity); return arraysEqual(p, n) ? p : n; });
      setCountries((p) => { const n = pruneSelected(p, nCountry); return arraysEqual(p, n) ? p : n; });
    }, 250);
    return () => window.clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, funding, deadline, locations, funderTypes, sectors, towns, cities, countries, fundingOpts, deadlineOpts]);

  // ── Reset counter effect ──────────────────────────────────────────────────
  React.useEffect(() => {
    if (resetCounter === 0) return;
    doSearch(1, "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetCounter]);

  // ─────────────────────────────────────────────────────────────────────────
  // ✅ FIX: Auto-search when filters change (runs AFTER state is committed)
  //
  // Previously, onSearch was called via setTimeout inside onChange handlers.
  // That meant doSearch ran with stale closure values (old filter state).
  // By using useEffect, React guarantees state is fully committed before
  // this effect fires — so doSearch always reads the correct, updated values.
  //
  // We skip the very first run (mount) because the mount effect already
  // calls doSearch with the correct initial/restored state.
  // ─────────────────────────────────────────────────────────────────────────
  const prevFiltersRef = React.useRef({
    funding: initState.funding,
    deadline: initState.deadline,
    locations: initState.locations,
    funderTypes: initState.funderTypes,
    sectors: initState.sectors,
    towns: initState.towns,
    cities: initState.cities,
    countries: initState.countries,
  });

  React.useEffect(() => {
    // Skip until mount search has run at least once
    if (!mountedRef.current) return;

    const prev = prevFiltersRef.current;
    const changed =
      prev.funding !== funding ||
      prev.deadline !== deadline ||
      !arraysEqual(prev.locations, locations) ||
      !arraysEqual(prev.funderTypes, funderTypes) ||
      !arraysEqual(prev.sectors, sectors) ||
      !arraysEqual(prev.towns, towns) ||
      !arraysEqual(prev.cities, cities) ||
      !arraysEqual(prev.countries, countries);

    if (!changed) return;

    prevFiltersRef.current = { funding, deadline, locations, funderTypes, sectors, towns, cities, countries };
    doSearch(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funding, deadline, locations, funderTypes, sectors, towns, cities, countries]);

  const resetFilters = () => {
    setQ(""); setFunding("Any"); setDeadline("Any");
    setLocations([]); setFunderTypes([]); setSectors([]);
    setTowns([]); setCities([]); setCountries([]);
    setOrdering(""); setAiResults(null); setPage(1); setCount(0);
    sessionStorage.removeItem(STATE_KEY);
    setResetCounter((c) => c + 1);
  };

  // ── AI smart match ────────────────────────────────────────────────────────
  const loadAi = async () => {
    try {
      setAiLoading(true);
      setAiResults(null);
      const data = await aiSearchDiscoverList();
      setAiResults(data.results);
      setSavedIds(new Set(data.results.filter((r: any) => r?.saved === true).map((r: any) => r.id)));
      setSavedMap((prev) => {
        const m = new Map(prev);
        data.results.forEach((r: any) => {
          const sgid = r?.saved_grant_id ?? r?.saved_grant?.id ?? r?.saved_id ?? null;
          if (r?.saved === true && sgid != null) m.set(r.id, sgid);
        });
        return m;
      });
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message ||  "Failed to load recommendations");
      setAiResults(null);
    } finally {
      setAiLoading(false);
    }
  };

  // ── Save/unsave ───────────────────────────────────────────────────────────
  const onToggleSave = async (g: DiscoveryGrant) => {
    if (savingId) return;
    try {
      setSavingId(g.id);
      const eid = savedMap.get(g.id) ?? (g as any)?.saved_grant_id ?? (g as any)?.saved_grant?.id ?? (g as any)?.saved_id ?? null;
      const isSaved = g.saved === true || eid != null || savedIds.has(g.id);
      if (isSaved) {
        if (eid == null) { alert("Unable to unsave: missing saved_grant_id."); return; }
        await deleteSavedGrant(eid);
        setSavedIds((p) => { const n = new Set(p); n.delete(g.id); return n; });
        setSavedMap((p) => { const n = new Map(p); n.delete(g.id); return n; });
        (g as any).saved = false;
      } else {
        const resp: any = await saveDiscovery({ grant_id: g.id });
        const sgid = resp?.id ?? resp?.saved_grant_id ?? resp?.saved_grant?.id;
        setSavedIds((p) => new Set(p).add(g.id));
        if (sgid != null) setSavedMap((p) => { const n = new Map(p); n.set(g.id, sgid); return n; });
        (g as any).saved = true;
      }
    } finally { setSavingId(null); }
  };

  // ── Start application ─────────────────────────────────────────────────────
  const onStart = async (g: DiscoveryGrant) => {
    if (startingId) return;
    try {
      setStartingId(g.id);
      markNavAway();
      sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
      const saveResp: any = await saveDiscovery({ grant_id: g.id });
      const savedGrantId = saveResp?.id ?? saveResp?.saved_grant_id ?? saveResp?.saved_grant?.id;
      if (!savedGrantId) { alert("Could not get saved_grant_id"); return; }
      const fullDetails = await getGrantFullDetails(savedGrantId);
      nav(`/dashboard/write/${savedGrantId}`, {
        state: { saved_grant_id: savedGrantId, grant: { id: g.id, title: g.title ?? "Selected Grant" }, details: fullDetails },
      });
    } catch (e: any) {
      sessionStorage.removeItem(NAV_FLAG);
      alert(e?.response?.data?.error || e?.response?.data?.message ||  "Could not start application");
    } finally { setStartingId(null); }
  };

  const toggleExpandDesc = (id: string | number) => setExpandedDesc((p) => ({ ...p, [id]: !p[id] }));

  // ── Hover handlers ────────────────────────────────────────────────────────
  const onCardEnter = (g: DiscoveryGrant, el: HTMLElement) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => { setHoverAnchorEl(el); setHoveredGrant(g); }, 400);
  };
  const onCardLeave = () => { if (hoverTimer.current) clearTimeout(hoverTimer.current); setHoveredGrant(null); setHoverAnchorEl(null); };

  const list = aiResults ?? results;
  const isAI = Boolean(aiResults);
  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  const hasFilters = !!(q || funding !== "Any" || deadline !== "Any" || locations.length || funderTypes.length || sectors.length || towns.length || cities.length || countries.length);

  // ─────────────────────────────────────────────────────────────────────────
  // CARD RENDERERS
  // ─────────────────────────────────────────────────────────────────────────

  const renderSearchCard = (g: DiscoveryGrant) => {
    const title = g.title ?? "Untitled Grant";
    const tags = [g.funder, g.applicant_type, g.location, (g as any).city, (g as any).country].filter(Boolean) as string[];
    const amount = g.total_fund_amount != null ? String(g.total_fund_amount)
      : g.min_award != null || g.max_award != null ? `${g.min_award ?? 0}${g.max_award ? " - " + g.max_award : ""}` : "—";
    const deadlineLabel = g.close_date ? new Date(g.close_date).toLocaleDateString() : "—";
    const isSaved = g.saved === true || savedMap.has(g.id) || savedIds.has(g.id);
    const savingThis = savingId === g.id, startingThis = startingId === g.id;
    const words = (g.description ?? "").trim().split(/\s+/), tooLong = words.length > 50, isOpen = expandedDesc[g.id] === true;
    const shownDesc = isOpen ? g.description ?? "" : tooLong ? words.slice(0, 50).join(" ") + "..." : g.description ?? "";

    return (
      <div key={String(g.id)}
        className="rounded-[10px] border border-[#E2E5EA] bg-white shadow-sm p-4 hover:shadow-md transition-shadow"
        onMouseEnter={(e) => onCardEnter(g, e.currentTarget)}
        onMouseLeave={onCardLeave}>
        <h3 className="text-xl font-semibold">
          <a href={g.url ?? "#"} target="_blank" className="hover:text-[#0077B6] hover:underline transition" rel="noreferrer">{title}</a>
        </h3>
        <div className="mt-2 flex flex-wrap gap-2">{tags.map((t) => <Tag key={t}>{t}</Tag>)}</div>
        <p className="mt-2 text-md font-medium text-[#464B50]">
          {shownDesc}{" "}
          {tooLong && <button className="text-[#0077B6] underline underline-offset-2 text-sm font-medium ml-1 hover:text-[#005a8c]" onClick={() => toggleExpandDesc(g.id)}>{isOpen ? "View less" : "View more"}</button>}
        </p>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-6 text-sm text-[#777B7F]">
          <div className="inline-flex items-center gap-1.5"><span className="material-icons text-[16px]">schedule</span><span>Deadline:</span> {deadlineLabel}</div>
          <div className="inline-flex items-center gap-1.5 font-medium"><span className="material-icons text-[17px]">currency_pound</span>{amount}</div>
          <div className="flex items-center gap-3">
            <button disabled={savingThis} onClick={() => onToggleSave(g)} title={isSaved ? "Unsave" : "Save"}
              className="h-9 w-9 rounded-[8px] border border-[#D6DADF] grid place-items-center text-[#374151] hover:bg-black/[0.03] disabled:opacity-60 transition-colors">
              <span className="material-icons text-[18px]">{isSaved ? "bookmark" : "bookmark_border"}</span>
            </button>
            <button disabled={startingThis} onClick={() => onStart(g)}
              className="h-9 px-3 rounded-lg bg-[#0077B7] text-white text-sm disabled:opacity-60 hover:bg-[#005a8c] transition-colors">
              {startingThis ? "Starting..." : "Start Application"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAICard = (g: DiscoveryGrant) => {
    const title = g.title ?? "Untitled Grant";
    const tags = [g.funder, g.applicant_type, g.location, (g as any).city, (g as any).country].filter(Boolean) as string[];
    const amount = g.total_fund_amount != null ? String(g.total_fund_amount)
      : g.min_award != null || g.max_award != null ? `${g.min_award ?? 0}${g.max_award ? " - " + g.max_award : ""}` : "—";
    const deadlineLabel = g.close_date ? new Date(g.close_date).toLocaleDateString() : "—";
    const isSaved = g.saved === true || savedMap.has(g.id) || savedIds.has(g.id);
    const savingThis = savingId === g.id, startingThis = startingId === g.id;
    const words = (g.description ?? "").trim().split(/\s+/), tooLong = words.length > 50, isOpen = expandedDesc[g.id] === true;
    const shownDesc = isOpen ? g.description ?? "" : tooLong ? words.slice(0, 50).join(" ") + "..." : g.description ?? "";
    const score = normalizeScore((g as any)?.match_score ?? (g as any)?.score ?? (g as any)?.ai_score);
    const reasons = aiReasonChips(g);

    return (
      <div key={String(g.id)}
        className="bg-gradient-to-r from-primary/70 via-teal-400/50 to-sky-400/50 rounded-xl p-[1px] hover:shadow-lg transition-shadow"
        onMouseEnter={(e) => onCardEnter(g, e.currentTarget)}
        onMouseLeave={onCardLeave}>
        <div className="rounded-[14px] border border-primary/70 bg-white shadow-sm p-4 relative">
          <div className="absolute -top-3 -left-3">
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/90 text-emerald-800 text-xs font-semibold px-2.5 py-1 shadow-sm">
              <span className="material-icons text-[14px]">auto_awesome</span>AI Smart Match
            </span>
          </div>
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-xl font-semibold text-[#0B1F17]">
              <a href={g.url ?? "#"} target="_blank" className="hover:text-emerald-700 hover:underline transition" rel="noreferrer">{title}</a>
            </h3>
            {score !== null && (
              <div className="flex items-center gap-2 min-w-[160px]">
                <span className="text-xs text-[#6B7280]">Score</span>
                <div className="w-24 h-2.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${score}%` }} /></div>
                <span className="text-xs font-semibold text-[#0B1F17]">{score}%</span>
              </div>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">{tags.map((t) => <Tag key={t}>{t}</Tag>)}</div>
          {!!reasons.length && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-emerald-700">Why this match:</span>
              {reasons.map((r, i) => <span key={r + i} className="text-xs rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5">{r}</span>)}
            </div>
          )}
          <p className="mt-2 text-[15px] font-medium text-[#1F2937]">
            {shownDesc}{" "}
            {tooLong && <button className="text-emerald-700 underline underline-offset-2 text-sm font-medium ml-1" onClick={() => toggleExpandDesc(g.id)}>{isOpen ? "View less" : "View more"}</button>}
          </p>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-6 text-sm text-[#374151]">
            <div className="inline-flex items-center gap-1.5"><span className="material-icons text-[16px] text-emerald-700">schedule</span><span>Deadline:</span> {deadlineLabel}</div>
            <div className="inline-flex items-center gap-1.5 font-semibold"><span className="material-icons text-[17px] text-emerald-700">currency_pound</span>{amount}</div>
            <div className="flex items-center gap-3">
              <button disabled={savingThis} onClick={() => onToggleSave(g)} title={isSaved ? "Unsave" : "Save"}
                className="h-9 w-9 rounded-[8px] border border-emerald-200 grid place-items-center text-emerald-800 hover:bg-emerald-50 disabled:opacity-60 transition-colors">
                <span className="material-icons text-[18px]">{isSaved ? "bookmark" : "bookmark_border"}</span>
              </button>
              <button disabled={startingThis} onClick={() => onStart(g)}
                className="h-9 px-3 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-60 transition-colors">
                {startingThis ? "Starting..." : "Start Application"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="mx-auto px-5 xl:px-7 py-6">
      {/* {hoveredGrant && <HoverCard grant={hoveredGrant} anchorEl={hoverAnchorEl} />} */}

      <div className="mb-8">
        <h1 className="text-[32px] font-semibold text-[#111]">Grant Discovery</h1>
        <p className="text-md text-[#6B7280]">Find the perfect opportunities for your projects.</p>
      </div>

      <div className="mb-6">
        <PrivateFundingWorkspace
          userKey={userKey}
          title="Private funding workspace"
          subtitle="Save private leads and manually added opportunities alongside public discovery."
          onUseInGrantWriter={(item) =>
            nav("/dashboard/write", { state: { privateFundingId: item.id } })
          }
        />
      </div>

      <div className="rounded-[10px] border border-[#E2E5EA] bg-white shadow-sm p-4 mb-6">
        {/* Search bar */}
        <div className="flex gap-3">
          <div className="flex-1 h-10 rounded-[8px] border border-[#D6DADF] bg-white flex items-center px-3 text-sm">
            <span className="material-icons text-[#9AA0A6] mr-2">search</span>
            <input className="w-full outline-none placeholder:text-[#9AA0A6] text-sm"
              placeholder="Search by keyword, funder, or category…" value={q}
              onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch(1)} />
          </div>
          <button onClick={() => doSearch(1)} className="h-10 px-4 rounded-lg bg-primary text-sm font-medium hover:bg-primary/90 transition-colors">Search</button>
          <button onClick={resetFilters} className="h-10 px-4 rounded-lg border border-[#D6DADF] bg-white text-sm font-medium hover:bg-gray-50 transition-colors">Reset</button>
        </div>

        {/* Filter grid */}
        {!filtersLoaded ? <SkeletonFilters /> : (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* ── SelectBlock no longer needs onSearch prop ── */}
            <SelectBlock label="Funding Amount" options={fundingOpts} value={funding} onChange={setFunding} />
            <SelectBlock label="Deadline" options={deadlineOpts} value={deadline} onChange={setDeadline} />
            {/* ── CheckboxMultiSelect uses onDone instead of onSearch ── */}
            <CheckboxMultiSelect label="Location" options={locationOpts} values={locations} onChange={setLocations} onDone={() => doSearch(1)} />
            <CheckboxMultiSelect label="Funder Type" options={funderTypeOpts} values={funderTypes} onChange={setFunderTypes} onDone={() => doSearch(1)} />
            <CheckboxMultiSelect label="Sector" options={sectorOpts} values={sectors} onChange={setSectors} onDone={() => doSearch(1)} />
            <CheckboxMultiSelect label="Town" options={townOpts} values={towns} onChange={setTowns} onDone={() => doSearch(1)} />
            <CheckboxMultiSelect label="City" options={cityOpts} values={cities} onChange={setCities} onDone={() => doSearch(1)} />
            <CheckboxMultiSelect label="Country" options={countryOpts} values={countries} onChange={setCountries} onDone={() => doSearch(1)} />
          </div>
        )}

        {/* Active filter chips */}
        {hasFilters && (
          <div className="mt-3 flex flex-wrap gap-1.5 items-center">
            <span className="text-xs text-[#6B7280] font-medium mr-1">Active:</span>
            {q && <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">{q}</span>}
            {funding !== "Any" && <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">{funding}</span>}
            {deadline !== "Any" && <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">{deadline}</span>}
            {locations.map((v) => <span key={v} className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">{v}</span>)}
            {funderTypes.map((v) => <span key={v} title={v} className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5 max-w-[180px] truncate">{v}</span>)}
            {sectors.map((v) => <span key={v} className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">{v}</span>)}
            {towns.map((v) => <span key={v} className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">{v}</span>)}
            {cities.map((v) => <span key={v} className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">{v}</span>)}
            {countries.map((v) => <span key={v} className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">{v}</span>)}
            <button onClick={resetFilters} className="text-xs text-red-600 underline hover:no-underline ml-1">Clear all</button>
          </div>
        )}

        {/* AI banner */}
        <div className={`mt-4 rounded-lg ${aiResults ? "bg-primary/70" : "bg-[#C0EFC0]"} text-[#17331F] px-4 py-4 flex flex-col md:flex-row md:items-center gap-5`}>
          <span className={`material-icons border border-black rounded-full shrink-0 bg-black ${aiResults ? "text-primary/70" : "text-primary"} h-[40px] w-[40px] flex items-center justify-center text-[22px]`}>auto_awesome</span>
          <div className="text-sm flex items-center gap-3 flex-wrap">
            <div className="font-medium text-[20px]">AI-Powered smart recommendation</div>
            <div className="text-[#777B7F] text-base">{aiResults ? `${aiResults.length} matches found.` : "We can fetch grants that strongly match your profile."}</div>
            <br />
            <button className="underline underline-offset-2 text-base font-medium hover:bg-black p-1 rounded hover:text-primary transition-colors" onClick={loadAi} disabled={aiLoading}>
              {aiLoading ? "Loading…" : "View smart match"}
            </button>
            {aiResults && (
              <button className="ml-2 underline underline-offset-2 text-base hover:bg-black p-1 rounded hover:text-primary transition-colors" onClick={() => { setAiResults(null); doSearch(1); }}>
                Back to search results
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Results header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-[32px] font-semibold text-[#111]">{isAI ? "AI Smart Matches" : "Grant Opportunities"}</h2>
          {!isAI && count > 0 && (
            <p className="text-sm text-[#6B7280]">
              {count.toLocaleString()} result{count !== 1 ? "s" : ""}
              {hasFilters ? " (filtered)" : ""}
              {" · Page "}{page}{" of "}{totalPages}
            </p>
          )}
        </div>
        {!isAI && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-[#777B7F]">Sort by:</span>
            <div className="relative min-w-[160px]">
              <select className="h-9 w-full cursor-pointer appearance-none rounded-lg border border-black/10 bg-white pr-8 pl-3 text-sm outline-none"
                value={ordering} onChange={(e) => { const next = e.target.value; setOrdering(next); doSearch(1, next); }}>
                <option value="" disabled>Select sort option</option>
                {orderingOpts.map((o) => <option key={(o.value || o.label) + "_ord"} value={o.value ?? ""}>{o.label}</option>)}
              </select>
              <span className="material-icons pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[18px] text-[#6B7280]">expand_more</span>
            </div>
          </div>
        )}
      </div>

      {error && <div className="text-sm text-red-600 mb-2">{error}</div>}

      <div className="space-y-3">
        {loading || aiLoading ? (
          [...Array(5)].map((_, i) => <SkeletonGrantCard key={i} />)
        ) : (
          <>
            {list.map((g) => (isAI ? renderAICard(g) : renderSearchCard(g)))}
            {!list.length && <div className="text-sm text-[#6B7280]">No grants found. Try different filters.</div>}
          </>
        )}
      </div>

      {!isAI && !loading && !aiLoading && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPrev={() => page > 1 && doSearch(page - 1)}
          onNext={() => page < totalPages && doSearch(page + 1)}
          onJump={(pg) => pg !== page && doSearch(pg)}
        />
      )}
    </div>
  );
}
